import { Link, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import { toMoney } from "../../utils/toMoney";
import { formatDate } from "../../utils/toDate";
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import CustomButton from "../CustomButton";
import CustomCheckbox from "../CustomCheckbox";
import { useEffect, useRef, useState } from "react";
import { RedFlagIndicator } from "../RedFlagIndicator";

export const TABLE_CELL_TYPES = Object.freeze({
  LINK: "link",
  TEXT: "text",
  DATE: "date",
  MONEY: "money",
  NOTES: "notes",
  ELEMENT: "element",
  NO_RENDER: "no_render",
});

export default function RoundedTable({
  titles,
  rows,
  fullData,
  selectedRows,
  setSelectedRows,
  withCheckbox,
  withRedFlag,
  pagination,
  setPagination,
  onSelectAll = null,
  mobileEffect = false,
  syncPageWithUrl = true,
  pageParam = "page",
  includeReturnTo = true,
  returnToParam = "returnTo",
}) {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const didInitFromUrlRef = useRef(false);

  const returnTo = `${location.pathname}${location.search}`;

  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = async () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      let allCustomers = [];

      if (onSelectAll) {
        allCustomers = await onSelectAll();
      } else {
        allCustomers = fullData;
      }

      const dataWithId = allCustomers.map((item) => ({
        id: item.id,
        data: item,
      }));

      setSelectedRows(dataWithId);
    }
  };

  useEffect(() => {
    const allSelected =
      fullData?.length > 0 &&
      fullData.every((item) =>
        selectedRows.some((selected) => selected.id === item.id)
      );
    setSelectAll(allSelected);
  }, [selectedRows, fullData]);

  useEffect(() => {
    if (!syncPageWithUrl || !pagination || !setPagination) return;

    const pageFromUrl = parseInt(searchParams.get(pageParam) || "1", 10);

    if (!didInitFromUrlRef.current) {
      didInitFromUrlRef.current = true;

      if (!Number.isNaN(pageFromUrl) && pageFromUrl > 0) {
        setPagination((prev) => {
          if (!prev) return prev;
          if (prev.currentPage === pageFromUrl) return prev;
          return { ...prev, currentPage: pageFromUrl };
        });
      }
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(pageParam, String(pagination.currentPage || 1));
        return next;
      },
      { replace: true }
    );
  }, [
    syncPageWithUrl,
    pagination?.currentPage,
    pageParam,
    searchParams,
    setPagination,
    setSearchParams,
  ]);

  const handleSelectOne = (id) => {
    const rowData = { id, data: fullData.find((item) => item.id === id) };

    setSelectedRows((prevSelected) => {
      const isSelected = prevSelected.some((item) => item.id === id);

      if (isSelected) {
        return prevSelected.filter((item) => item.id !== id);
      } else {
        return [...prevSelected, rowData];
      }
    });
  };

  const buildLinkTo = (to) => {
    if (!includeReturnTo || !to || typeof to !== "string") return to;

    if (to.includes(`${returnToParam}=`)) return to;

    const encoded = encodeURIComponent(returnTo);
    const glue = to.includes("?") ? "&" : "?";
    return `${to}${glue}${returnToParam}=${encoded}`;
  };

  const renderTableCell = (row) => {
    return row?.map((cell, index, array) => {
      const value = cell?.value ?? "-";
      const sign = cell?.sign || "";
      const currency = cell?.currency || "";
      const bold = cell?.bold ? "t-bold_bold" : "";
      const firstCell =
        !withCheckbox && index === 0
          ? "the-rounded-first-cell"
          : withRedFlag && index === 0
          ? "the-rounded-first-cell-with-red-flag"
          : "";

      const lastCell =
        index === array.length - 1 ? "the-rounded-last-cell" : "";
      const roundedCorners = `${firstCell} ${lastCell}`;
      const noWrapText = cell.noWrapText ? "" : "t-eclipse-text";
      const keepOnMobile =
        index > 0 ? (cell.keepOnMobile ? "" : "mobile-hider") : "";

      switch (cell.type) {
        case TABLE_CELL_TYPES.LINK:
          return (
            <td
              className={`cell ${bold} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <Link
                to={buildLinkTo(cell?.to)}
                className={`link ${noWrapText}`}
                state={
                  includeReturnTo
                    ? { ...(cell?.state || {}), returnTo }
                    : cell?.state
                }
              >
                {value}
              </Link>
            </td>
          );
        case TABLE_CELL_TYPES.TEXT:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <p className={noWrapText}>{value}</p>
            </td>
          );
        case TABLE_CELL_TYPES.MONEY:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {toMoney(value) || 0} {currency}
            </td>
          );
        case TABLE_CELL_TYPES.DATE:
          return (
            <td
              className={`cell ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value ? formatDate(value) : "-"}
            </td>
          );
        case TABLE_CELL_TYPES.NOTES:
          return (
            <td
              className={`cell ${bold} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <p className={`notes-cell ${noWrapText}`}>{value}</p>
            </td>
          );
        case TABLE_CELL_TYPES.ELEMENT:
          return (
            <td
              className={`cell ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value}
            </td>
          );
        case TABLE_CELL_TYPES.NO_RENDER:
          return null;
        default:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <div className={noWrapText}>{value}</div>
            </td>
          );
      }
    });
  };

  return (
    <>
      <section className="rounded-table-container">
        <table className="i-table">
          <thead className="table-head">
            <tr>
              {withCheckbox && (
                <th className="bold-text">
                  <CustomCheckbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </th>
              )}
              {withRedFlag && <th className="bold-text"></th>}
              {titles?.map((column, index) =>
                index > 0 ? (
                  column.content ? (
                    column.content
                  ) : (
                    <th
                      key={index}
                      className={`cell-title ${
                        column?.keepOnMobile ? "" : "mobile-hider"
                      }`}
                    >
                      {t(column?.value)}
                    </th>
                  )
                ) : (
                  <th key={index} className={`bold-text cell-title ${column}`}>
                    {t(column?.value)}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="table-body">
            {rows?.map((row, index) => (
              <tr key={index}>
                {withCheckbox && (
                  <td className={`cell the-rounded-first-cell`}>
                    <CustomCheckbox
                      checked={selectedRows?.some(
                        (item) => item.data.id === fullData[index].id
                      )}
                      onChange={() => handleSelectOne(fullData[index].id, row)}
                    />
                  </td>
                )}
                {row[0].isRedFlag && withRedFlag ? (
                  <td
                    className={`cell the-rounded-first-cell-with-red-flag the-rounded-first-cell`}
                  >
                    <RedFlagIndicator isRedFlag={true} />
                  </td>
                ) : withRedFlag ? (
                  <td>
                    <span></span>
                  </td>
                ) : null}

                {renderTableCell(row)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      
      {pagination && (
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }))
              }
              disabled={pagination.currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
              mobileEffect={mobileEffect}
            />
            <span className="pagination-location">
              {t("page")}: {pagination.currentPage} - {pagination.totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }))
              }
              disabled={pagination.currentPage === pagination.totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
              mobileEffect={mobileEffect}
            />
          </div>
          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalRecords")}: {pagination.totalRecords}
            </span>
          </div>
        </section>
      )}
    </>
  );
}
